import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Wallet,
  Users,
  MapPin,
  LogOut,
  UserCircle,
  Bell,
  ChevronDown,
  User,
  Award,
  Home,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Trophy,
} from "lucide-react";
import PanelLayout from "@/components/shared/panel/PanelLayout";
import NotificationBell from "@/components/shared/notifications/NotificationBell";
import type { PanelNavEntry } from "@/components/shared/panel/PanelSidebar";
import { useLogout } from "@/hooks/useLogout";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { getInitials } from "@/lib/account-profile-utils";

const navItems: PanelNavEntry[] = [
  { title: "Overview", url: "/account", icon: LayoutDashboard },
  {
    type: "group",
    title: "Profile",
    icon: User,
    children: [
      { title: "Personal Info", url: "/account/profile" },
      { title: "Member ID", url: "/account/member-id" },
      { title: "KYC Verification", url: "/account/kyc" },
    ],
  },
  { title: "My Orders", url: "/account/orders", icon: ShoppingCart },
  {
    type: "group",
    title: "Wallet",
    icon: Wallet,
    children: [
      { title: "Balance & P2P", url: "/account/wallet" },
      { title: "Transactions", url: "/account/wallet/transactions" },
    ],
  },
  { title: "Rewards & BV", url: "/account/rewards", icon: Award },
  { title: "Rank Rewards", url: "/account/rank-rewards", icon: Trophy },
  {
    type: "group",
    title: "My Team",
    icon: Users,
    children: [
      { title: "Direct Referral", url: "/account/team/direct" },
      { title: "Level List", url: "/account/team/level" },
    ],
  },
  { title: "Reports", url: "/account/reports", icon: FileText },
  { title: "Addresses", url: "/account/addresses", icon: MapPin },
  { title: "Notifications", url: "/account/notifications", icon: Bell },
];

const pageTitles: Record<string, string> = {
  "/account": "Overview",
  "/account/profile": "Personal Info",
  "/account/member-id": "Member ID",
  "/account/kyc": "KYC Verification",
  "/account/orders": "My Orders",
  "/account/wallet": "Wallet",
  "/account/wallet/transactions": "Wallet Transactions",
  "/account/rewards": "Rewards & BV",
  "/account/referrals": "Direct Team",
  "/account/team/direct": "Direct Team",
  "/account/team/level": "Level Team",
  "/account/addresses": "Addresses",
  "/account/notifications": "Notifications",
  "/account/rank-rewards": "Rank Rewards",
};

const AccountLayout = () => {
  const location = useLocation();
  const { user } = useAuth();
  const logout = useLogout();

  const displayName = user?.name ?? "Account";
  const displayEmail = user?.email ?? "";
  const initials = getInitials(displayName);
  const currentPageTitle =
    pageTitles[location.pathname] || (location.pathname.startsWith("/account/reports") ? "Reports" : "Account");

  const topbarActions = (
    <>
      <NotificationBell scope="me" viewAllPath="/account/notifications" />
      <Separator orientation="vertical" className="mx-0.5 hidden h-7 sm:block" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="glass-chip h-10 gap-2 rounded-xl px-1.5 hover:bg-transparent sm:px-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-primary-foreground ring-2 ring-primary/20">
              {initials}
            </div>
            <div className="hidden flex-col items-start leading-tight lg:flex">
              <span className="text-[13px] font-semibold text-foreground">{displayName}</span>
              <span className="text-[10px] text-muted-foreground">Customer</span>
            </div>
            <ChevronDown className="hidden h-3 w-3 text-muted-foreground sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="z-50 w-[min(220px,calc(100vw-1rem))] rounded-xl border border-border bg-popover p-1.5 shadow-2xl"
        >
          <div className="mb-1 rounded-lg bg-secondary/40 px-2.5 py-3">
            <p className="text-sm font-bold text-foreground">{displayName}</p>
            <p className="text-[11px] text-muted-foreground">{displayEmail}</p>
          </div>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to="/account/profile">
              <User className="h-4 w-4 text-muted-foreground" />
              Personal Info
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to="/account/member-id">
              <BadgeCheck className="h-4 w-4 text-muted-foreground" />
              Member ID
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to="/account/kyc">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              KYC Verification
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to="/account/wallet">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              Wallet
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-1" />
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to="/">
              <Home className="h-4 w-4 text-muted-foreground" />
              Back to Store
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive"
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <PanelLayout
      brand={{
        icon: UserCircle,
        title: "My Account",
        subtitle: displayName,
        url: "/account",
      }}
      navItems={navItems}
      footer={{ label: "Sign Out", url: "/" }}
      breadcrumbPrefix="Account"
      pageTitle={currentPageTitle}
      sectionLabel="MENU"
      topbarActions={topbarActions}
    />
  );
};

export default AccountLayout;
